/* ------------------------------------------------------------------ *
 * مار پله — how things LOOK (card-86, batch B1). Pure data.
 *
 * The engine's boards say only WHERE each snake and ladder is; the look
 * comes from its index here: snake i → SNAKE_SKINS[i % 6], ladder i →
 * LADDER_MATERIALS[i % 3]. A shipped board's order is frozen
 * (`layouts.ts`), so a board always looks the same on every phone.
 * ------------------------------------------------------------------ */

/** One seat's piece colour: `base` for the disc and the seat strip, `light` for its highlight. */
export interface PieceTone {
  readonly base: string;
  readonly light: string;
}

/**
 * The pieces, seat-indexed (seat i always plays colour i — no colour stage). Chosen to stand out
 * on BOTH cell tones and against every snake skin: red, blue, gold, violet — never a green on a
 * jungle board. B2's seat strip reads `LADDERS_PIECE_COLORS` so the strip and the board agree.
 */
export const LADDERS_PIECE_TONES: readonly PieceTone[] = [
  { base: "#e5564c", light: "#ff9a8f" }, // red
  { base: "#3d9be0", light: "#9fd8ff" }, // blue
  { base: "#e9c349", light: "#fff0a8" }, // gold
  { base: "#9a68e0", light: "#d4b8ff" }, // violet
];

/** The pieces' colours, seat-indexed — the board and B2's seat strip use the same. */
export const LADDERS_PIECE_COLORS: readonly string[] = LADDERS_PIECE_TONES.map((t) => t.base);

/** A seat's tone (seats past the palette wrap — the engine never seats more than four). */
export function pieceTone(seat: number): PieceTone {
  return LADDERS_PIECE_TONES[
    ((seat % LADDERS_PIECE_TONES.length) + LADDERS_PIECE_TONES.length) % LADDERS_PIECE_TONES.length
  ]!;
}

/* ---- the board's colours, PER LOOK (round 2: three looks — `styles.ts` picks one) ----------- */

/** One look's board colours: the two alternating cell tones, the grout, the numbers, the pad. */
export interface BoardTones {
  /** The darker of the two alternating cell tones (`glow` is its lit centre). */
  readonly dark: { readonly base: string; readonly glow: string };
  /** The lighter one. */
  readonly light: { readonly base: string; readonly glow: string };
  /** Behind the grid, between the cells. */
  readonly grout: string;
  /** The numbers' ink, and the halo painted under it (so a number reads over any art). */
  readonly ink: string;
  readonly halo: string;
  /** The start pad's plate (under the bottom-left corner) and its four slot rings. */
  readonly pad: string;
  readonly padOpacity: number;
  readonly padSlot: string;
}

/* The JUNGLE look — SAMPLED FROM THE BACKGROUND. `ladders-scene.cjs` measured the picture: the misty
 * middle averages #454e38, the board's own area #505436, the deep foliage #32381d, the lit grass
 * #b1973d. The two cell tones are those greens and that grass, deepened and creamed just enough to
 * keep the numbers readable. (A second PICTURE look draws these too unless its measurements carry
 * their own `cells` — `styles.ts`.) */
export const LADDERS_BOARD_TONES = {
  /** The deep jungle cells. */
  dark: { base: "#2f4a2c", glow: "#3d5c37" },
  /** The warm olive-cream cells. */
  light: { base: "#a9a46c", glow: "#c4bf86" },
  /** Behind the grid, between the cells. */
  grout: "#1d2a17",
  /** The numbers: cream ink with a dark halo, so they read on either tone and over any art. */
  ink: "#fff8de",
  halo: "#1a2412",
  /** The start pad: a dark plate, so the pieces on 0 read against the picture. */
  pad: "#14200f",
  padOpacity: 0.82,
  padSlot: "#fff3c8",
} as const satisfies BoardTones;

/**
 * «اورجینال» (`classic`, round 2) — a CLASSIC printed board with no picture: cream and soft sage
 * cells, dark ink with a cream halo, a brown grout line. Light enough that every snake skin and
 * ladder material (their dark outlines) stands out, and the gold 100 still glows over it.
 */
export const CLASSIC_BOARD_TONES = {
  dark: { base: "#c9d9a6", glow: "#d9e6ba" },
  light: { base: "#f6edd2", glow: "#fffaea" },
  grout: "#8a6a3e",
  ink: "#2a1f10",
  halo: "#fffaf0",
  pad: "#5a4123",
  padOpacity: 0.9,
  padSlot: "#fff3c8",
} as const satisfies BoardTones;

/* ---- snakes: ≥ 6 skins (colour + pattern) ------------------------------------------------- */

export type SkinPattern = "diamonds" | "bands" | "spots" | "stripes" | "scales" | "zigzag";

export interface SnakeSkin {
  readonly pattern: SkinPattern;
  /** The body. */
  readonly body: string;
  /** The outline and the head's shading. */
  readonly edge: string;
  /** The pattern's colour. */
  readonly mark: string;
  /** A second pattern colour (bands alternate; others may ignore it). */
  readonly mark2: string;
  /** The highlight along the back. */
  readonly sheen: string;
}

/** snake i → SNAKE_SKINS[i % 6]: no two snakes on a board look alike up to six. */
export const SNAKE_SKINS: readonly SnakeSkin[] = [
  // a golden python with brown diamonds
  {
    pattern: "diamonds",
    body: "#d8a62a",
    edge: "#4f3300",
    mark: "#6e420f",
    mark2: "#f3d36b",
    sheen: "#ffe9a3",
  },
  // a coral snake: red with black-and-yellow bands
  {
    pattern: "bands",
    body: "#d23f30",
    edge: "#3a0a05",
    mark: "#1d1410",
    mark2: "#f2cf45",
    sheen: "#ff9d8c",
  },
  // an orange cat-snake with dark spots
  {
    pattern: "spots",
    body: "#e2802c",
    edge: "#4a1f00",
    mark: "#4b2004",
    mark2: "#ffc27a",
    sheen: "#ffc890",
  },
  // a blue racer with a pale back stripe
  {
    pattern: "stripes",
    body: "#2f80b6",
    edge: "#0a2638",
    mark: "#c6ecff",
    mark2: "#164e73",
    sheen: "#9fd6f5",
  },
  // a violet snake with pale scales
  {
    pattern: "scales",
    body: "#8550bd",
    edge: "#2a0c45",
    mark: "#dcc2ff",
    mark2: "#5b2f8c",
    sheen: "#c9a6f2",
  },
  // a green viper with a yellow zigzag
  {
    pattern: "zigzag",
    body: "#4fae45",
    edge: "#0f3a0b",
    mark: "#f3e36a",
    mark2: "#2b6b24",
    sheen: "#a9e59a",
  },
];

/* ---- ladders: 3 materials ------------------------------------------------------------------ */

export type LadderKind = "wood" | "rope" | "metal";

export interface LadderMaterial {
  readonly kind: LadderKind;
  /** The rails. */
  readonly rail: string;
  /** The rails' lit edge. */
  readonly railLight: string;
  /** The rungs. */
  readonly rung: string;
  /** Every outline. */
  readonly edge: string;
  /** Rope twist marks / metal rivets / wood grain. */
  readonly detail: string;
  /** Rail thickness, in board units. */
  readonly railW: number;
  /** Rung thickness, in board units. */
  readonly rungW: number;
}

/** ladder i → LADDER_MATERIALS[i % 3]: wood, rope, metal. */
export const LADDER_MATERIALS: readonly LadderMaterial[] = [
  {
    kind: "wood",
    rail: "#8d5a2b",
    railLight: "#c99050",
    rung: "#a8703a",
    edge: "#33200c",
    detail: "#6b4220",
    railW: 0.1,
    rungW: 0.075,
  },
  {
    kind: "rope",
    rail: "#d9c08a",
    railLight: "#f2e2b4",
    rung: "#b48c53",
    edge: "#4a3a1e",
    detail: "#8f7444",
    railW: 0.085,
    rungW: 0.09,
  },
  {
    kind: "metal",
    rail: "#a3aeb6",
    railLight: "#eef4f7",
    rung: "#c3ccd2",
    edge: "#323b42",
    detail: "#5d6870",
    railW: 0.085,
    rungW: 0.06,
  },
];

export function snakeSkin(index: number): SnakeSkin {
  return SNAKE_SKINS[index % SNAKE_SKINS.length]!;
}

export function ladderMaterial(index: number): LadderMaterial {
  return LADDER_MATERIALS[index % LADDER_MATERIALS.length]!;
}
